import {
	createElement,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { FeatureGuide, useFeatureGuide } from "guides/components";
import type { HelpStep } from "./types";
import { waitForTarget } from "./waitForTarget";

export interface UseGuidedTourOptions {
	/** Steps to drive the tour. */
	steps: HelpStep[];
	/** Fires once the tour completes or is dismissed. */
	onAfterEnd?: () => void;
	/** Fires before the tour starts. Return `false` to cancel. */
	onBeforeStart?: () => boolean | void | Promise<boolean | void>;
}

export interface UseGuidedTourResult {
	/** `true` while the tour is mounted (Joyride is rendered into the DOM). */
	isActive: boolean;
	/**
	 * Portal node that hosts Joyride while the tour is active.
	 * Render it anywhere in your tree (or just spread it once); it will
	 * `null`-out when the tour is not active.
	 */
	TourPortal: ReactNode;
	/** Start the tour. Resolves once Joyride has been requested to run. */
	start: () => Promise<void>;
	/**
	 * PRO-757 crash fix — explicitly ends a tour that may still be running,
	 * WITHOUT waiting for/relying on the caller unmounting whatever rendered
	 * `TourPortal`. A caller that swaps its own step/screen out from under a
	 * live tour (so the step's targets disappear) must call this BEFORE that
	 * happens, or the tour is left actively watching targets that no longer
	 * exist — the crash the fix addresses. Flips `run` false first (same path
	 * STATUS.FINISHED/SKIPPED/ACTIONS.CLOSE already use via `guide.stop()`),
	 * letting the existing mount/run-consistency effect below unmount Joyride
	 * gracefully over the following render, rather than yanking it out of the
	 * tree while still `run=true`.
	 */
	stop: () => void;
}

/**
 * Headless hook that owns the lifecycle of a `HelpStep[]` guided tour.
 *
 * Responsibilities:
 *  - Two-phase mount so Joyride sees `run` transition `false → true`.
 *  - Wrap `next`/`prev` so a leaving step's `onAfterStep` and the entering
 *    step's `onBeforeStep` complete, AND the new target is laid out, before
 *    Joyride's step index changes — eliminating the "far-left fallback"
 *    placement when an effect (e.g. opening a drawer) mounts the next
 *    target asynchronously.
 *  - Unmount Joyride once the tour ends so it doesn't pollute body height.
 *
 * Use this directly if you need a custom trigger (e.g. a menu item or
 * banner button); otherwise reach for `<Help>` which composes this hook
 * with a default icon trigger.
 */
export const useGuidedTour = ({
	steps,
	onBeforeStart,
	onAfterEnd,
}: UseGuidedTourOptions): UseGuidedTourResult => {
	const guide = useFeatureGuide();
	const [mounted, setMounted] = useState(false);
	const [run, setRun] = useState(false);
	const previousIndex = useRef<number | null>(null);

	const hasSteps = steps.length > 0;

	const start = useCallback(async () => {
		if (!hasSteps) {
			return;
		}
		if (onBeforeStart) {
			const result = await onBeforeStart();
			if (result === false) {
				return;
			}
		}
		setMounted(true);
		setRun(false);
	}, [hasSteps, onBeforeStart]);

	// Phase 2: once Joyride is mounted with run=false, flip run=true on the
	// next frame so Joyride observes the transition.
	useEffect(() => {
		if (mounted && !run) {
			const frame = requestAnimationFrame(() => {
				setRun(true);
				guide.start();
			});
			return () => cancelAnimationFrame(frame);
		}
		return undefined;
	}, [mounted, run, guide]);

	// Unmount Joyride when the tour ends.
	useEffect(() => {
		if (mounted && run && !guide.isRunning) {
			setMounted(false);
			setRun(false);
			previousIndex.current = null;
			onAfterEnd?.();
		}
	}, [mounted, run, guide.isRunning, onAfterEnd]);

	// Fire `onBeforeStep` for the first step once the tour starts.
	useEffect(() => {
		if (
			guide.isRunning &&
			guide.stepIndex === 0 &&
			previousIndex.current === null
		) {
			previousIndex.current = 0;
			void steps[0]?.onBeforeStep?.();
		}
	}, [guide.isRunning, guide.stepIndex, steps]);

	// Wrap next/prev so step effects + target readiness gate the transition.
	const wrappedGuide = useMemo(
		() => ({
			...guide,
			next: async () => {
				const from = guide.stepIndex;
				const to = from + 1;
				await steps[from]?.onAfterStep?.();
				await steps[to]?.onBeforeStep?.();
				await waitForTarget(steps[to]?.target);
				previousIndex.current = to;
				guide.next();
			},
			prev: async () => {
				const from = guide.stepIndex;
				const to = from - 1;
				if (to < 0) {
					return;
				}
				await steps[from]?.onAfterStep?.();
				await steps[to]?.onBeforeStep?.();
				await waitForTarget(steps[to]?.target);
				previousIndex.current = to;
				guide.prev();
			},
		}),
		[guide, steps],
	);

	const TourPortal = mounted && hasSteps
		? createPortal(
				createElement(FeatureGuide, { guide: wrappedGuide, steps }),
				document.body,
			)
		: null;

	return {
		isActive: mounted,
		start,
		// `guide.stop` is already a stable (useCallback, `[]`-deps) reference from
		// `useFeatureGuide` — reused directly rather than re-wrapped, so a caller
		// depending on it in an effect array doesn't re-fire every render just
		// because `guide` itself is a fresh object literal each render.
		stop: guide.stop,
		TourPortal,
	};
};
